import React from "react";
import type {BalanceMovementItem} from "../api";

function reasonLabel(movement: BalanceMovementItem): string {
    switch (movement.reason) {
        case "payment_success":
            return "Успешный платёж";
        case "team_reset":
            return movement.merchantId ? "Расчёт с мерчантом" : "Сброс баланса команды";
        case "merchant_reset":
            return movement.teamId ? "Расчёт с командой" : "Сброс баланса мерчанта (админ)";
        // ДДС предоплатного мерчанта: реальные деньги, пришедшие или ушедшие
        // мимо заявок. Контрагента у таких строк нет — см. подпись «без атрибуции»
        // в разбивке по командам.
        case "manual_topup":
            return "Пополнение (ручное)";
        case "manual_withdrawal":
            return "Вывод (ручной)";
        case "blockchain_deposit":
            return "Пополнение TRC20 (авто)";
        case "backfill":
            return "Восстановлено из истории";
        case "backfill_correction":
            return "Корректировка восстановления";
        default:
            return movement.reason;
    }
}

function counterpartyName(movement: BalanceMovementItem): string {
    return movement.merchantName ?? movement.teamName ?? "—";
}

function counterpartyId(movement: BalanceMovementItem): string | undefined {
    return movement.merchantId ?? movement.teamId;
}

// У строки выписки два времени. «Записано» (createdAt) — момент, в который
// строка попала в книгу; по нему список и отсортирован, потому что «Баланс
// после» это нарастающий итог, снятый именно в этот момент. «Время площадки»
// (occurredAt) — когда описанное строкой событие произошло у источника; строки
// внешнего источника приезжают пачкой и позже, поэтому у них два времени
// расходятся на часы.
//
// Когда моменты совпадают — а у всего, что рождается внутри системы, они
// совпадают всегда — вторая ячейка пустая: повторить ту же дату рядом значит
// зашуметь колонку, в которую после этого перестанут смотреть.
function platformTime(movement: BalanceMovementItem): string {
    if (sameToTheSecond(movement.createdAt, movement.occurredAt)) return "";
    return formatMovementDate(movement.occurredAt);
}

// Секунда, а не отформатированная минута: строки одной пачки разведены
// микросекундами, и на минутах пачка выглядела бы одинаковой сама с собой.
function sameToTheSecond(left: string, right: string): boolean {
    if (left === right) return true;
    const a = new Date(left).getTime();
    const b = new Date(right).getTime();
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    return Math.floor(a / 1000) === Math.floor(b / 1000);
}

interface BalanceMovementsTableProps {
    items: BalanceMovementItem[];
    currency: string;
    loading: boolean;
    loadingMore?: boolean;
    error?: string;
    hasMore: boolean;
    onLoadMore: () => void;
    counterpartyLabel: string;
    footer?: React.ReactNode;
}

export function BalanceMovementsTable({
    items,
    currency,
    loading,
    loadingMore = false,
    error = "",
    hasMore,
    onLoadMore,
    counterpartyLabel,
    footer,
}: BalanceMovementsTableProps): JSX.Element {
    return (
        <section className="balance-movements-section">
            <div className="section-header">
                <h3>Движения</h3>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <div className="table-wrapper balance-movements-table-wrap">
                <table className="balance-movements-table">
                    <thead>
                    <tr>
                        <th>Записано</th>
                        <th>Время площадки</th>
                        <th>Причина</th>
                        <th>{counterpartyLabel}</th>
                        <th>Дельта</th>
                        <th>Баланс после</th>
                        <th>Актор</th>
                        <th>Платёж</th>
                    </tr>
                    </thead>
                    <tbody>
                    {items.map((movement) => (
                        <tr key={movement.id}>
                            {/* data-label — подпись колонки для строки-карточки
                                в кабинете трейдера на телефоне. */}
                            <td className="mono" data-label="Записано">{formatMovementDate(movement.createdAt)}</td>
                            <td className="mono muted" data-label="Время площадки">{platformTime(movement)}</td>
                            <td data-label="Причина">{reasonLabel(movement)}</td>
                            <td data-label={counterpartyLabel} title={counterpartyId(movement)}>{counterpartyName(movement)}</td>
                            <td className={`mono balance-delta ${movement.delta.trim().startsWith("-") ? "negative" : "positive"}`} data-label="Дельта">
                                {movement.delta} {currency}
                            </td>
                            <td className="mono" data-label="Баланс после">
                                {movement.balanceAfter === null ? "—" : `${movement.balanceAfter} ${currency}`}
                            </td>
                            <td data-label="Актор">{movement.actor || "system"}</td>
                            <td data-label="Платёж">{renderPaymentID(movement.paymentId)}</td>
                        </tr>
                    ))}
                    {!loading && items.length === 0 && (
                        <tr>
                            <td colSpan={8}>
                                <div className="empty-state compact">Движений пока нет.</div>
                            </td>
                        </tr>
                    )}
                    {loading && items.length === 0 && (
                        <tr>
                            <td colSpan={8}>
                                <div className="empty-state compact">Загрузка движений...</div>
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {hasMore && (
                <div className="balance-movements-actions">
                    <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={onLoadMore}
                        disabled={loading || loadingMore}
                    >
                        {loadingMore ? "Загрузка..." : "Показать ещё"}
                    </button>
                </div>
            )}
            {footer}
        </section>
    );
}

function renderPaymentID(paymentId: string | null): JSX.Element {
    if (!paymentId) return <span className="muted">—</span>;
    return (
        <span className="mono" title={paymentId}>
            {paymentId.slice(0, 8)}
        </span>
    );
}

function formatMovementDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}
